import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { maintenanceService } from '../services/api';
import type { MaintenanceRecord, MaintenanceFormData, MaintenanceStats, MaintenanceFilters } from '../types';

// Maintenance state interface
interface MaintenanceState {
  records: MaintenanceRecord[];
  stats: MaintenanceStats | null;
  loading: boolean;
  error: string | null;
}

// Initial state
const initialState: MaintenanceState = {
  records: [],
  stats: null,
  loading: false,
  error: null,
};

// Async thunks for maintenance
export const fetchMaintenanceRecords = createAsyncThunk(
  'maintenance/fetchRecords',
  async (params: MaintenanceFilters | undefined, { rejectWithValue }) => {
    try {
      const response = await maintenanceService.getAll(params);
      return response.data.data as MaintenanceRecord[];
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch maintenance records');
    }
  }
);

export const fetchMaintenanceStats = createAsyncThunk(
  'maintenance/fetchStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await maintenanceService.getStats();
      return response.data.data as MaintenanceStats;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch maintenance stats');
    }
  }
);

export const createMaintenanceRecord = createAsyncThunk(
  'maintenance/createRecord',
  async (data: MaintenanceFormData, { rejectWithValue }) => {
    try {
      const response = await maintenanceService.create(data);
      return response.data.data as MaintenanceRecord;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create maintenance record');
    }
  }
);

export const updateMaintenanceRecord = createAsyncThunk(
  'maintenance/updateRecord',
  async ({ id, data }: { id: number; data: Partial<MaintenanceFormData> }, { rejectWithValue }) => {
    try {
      const response = await maintenanceService.update(id, data);
      return response.data.data as MaintenanceRecord;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update maintenance record');
    }
  }
);

export const deleteMaintenanceRecord = createAsyncThunk(
  'maintenance/deleteRecord',
  async (id: number, { rejectWithValue }) => {
    try {
      await maintenanceService.delete(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete maintenance record');
    }
  }
);

export const updateMaintenanceStatus = createAsyncThunk(
  'maintenance/updateStatus',
  async ({ id, status }: { id: number; status: string }, { rejectWithValue }) => {
    try {
      const response = await maintenanceService.updateStatus(id, status);
      return response.data.data as MaintenanceRecord;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update maintenance status');
    }
  }
);

// Maintenance slice
const maintenanceSlice = createSlice({
  name: 'maintenance',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    addMaintenanceRecord: (state, action: PayloadAction<MaintenanceRecord>) => {
      state.records.unshift(action.payload);
    },
    removeMaintenanceRecord: (state, action: PayloadAction<number>) => {
      state.records = state.records.filter(record => record.id !== action.payload);
    },
    updateMaintenanceRecordInState: (state, action: PayloadAction<MaintenanceRecord>) => {
      const updatedRecord = action.payload;
      
      // Update in records array
      const recordIndex = state.records.findIndex(r => r.id === updatedRecord.id);
      if (recordIndex !== -1) {
        state.records[recordIndex] = updatedRecord;
      }
    },
  },
  extraReducers: (builder) => {
    // Fetch maintenance records
    builder
      .addCase(fetchMaintenanceRecords.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMaintenanceRecords.fulfilled, (state, action) => {
        state.loading = false;
        state.records = action.payload;
      })
      .addCase(fetchMaintenanceRecords.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Fetch maintenance stats
      .addCase(fetchMaintenanceStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMaintenanceStats.fulfilled, (state, action) => {
        state.loading = false;
        state.stats = action.payload;
      })
      .addCase(fetchMaintenanceStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Create maintenance record
      .addCase(createMaintenanceRecord.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createMaintenanceRecord.fulfilled, (state, action) => {
        state.loading = false;
        state.records.unshift(action.payload);
      })
      .addCase(createMaintenanceRecord.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Update maintenance record
      .addCase(updateMaintenanceRecord.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateMaintenanceRecord.fulfilled, (state, action) => {
        state.loading = false;
        const updatedRecord = action.payload;
        
        // Update in records array
        const recordIndex = state.records.findIndex(r => r.id === updatedRecord.id);
        if (recordIndex !== -1) {
          state.records[recordIndex] = updatedRecord;
        }
      })
      .addCase(updateMaintenanceRecord.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Delete maintenance record
      .addCase(deleteMaintenanceRecord.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteMaintenanceRecord.fulfilled, (state, action) => {
        state.loading = false;
        const deletedId = action.payload;
        state.records = state.records.filter(record => record.id !== deletedId);
      })
      .addCase(deleteMaintenanceRecord.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Update maintenance status
      .addCase(updateMaintenanceStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateMaintenanceStatus.fulfilled, (state, action) => {
        state.loading = false;
        const updatedRecord = action.payload;
        
        // Update in records array
        const recordIndex = state.records.findIndex(r => r.id === updatedRecord.id);
        if (recordIndex !== -1) {
          state.records[recordIndex] = updatedRecord;
        }
      })
      .addCase(updateMaintenanceStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, addMaintenanceRecord, removeMaintenanceRecord, updateMaintenanceRecordInState } = maintenanceSlice.actions;
export default maintenanceSlice.reducer;